// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * cm-coding: Administrators compare recorded document-processing volume with a
 * tunable estimate of manual work avoided. Keep recorded rollups distinct from
 * the client-side savings estimate so users cannot mistake an adjustable model
 * for measured spend or time. The shared Costs layout makes those operational
 * comparisons consistent without combining their data sources.
 */
import { useState, useCallback, useMemo } from 'react'
import { AxiosInstance } from 'axios'
import { useMonthlyMetrics } from '../hooks/useMetrics'
import { createMetricsApi, type MetricLevel } from '../services/metrics-api'
import { useAuth } from '@/subsystems/workspaces'
import { Alert, AlertDescription, SegmentedTabs } from '@/components/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui'
import { AlertTriangle, Download, Loader2 } from 'lucide-react'
import type { MonthlyMetricsResponse } from '../types'
import { MetricsTable } from './MetricsTable'
import { loadSavingsConfig, SavingsEstimate } from './SavingsEstimate'

interface MetricsDashboardProps {
  api_instance: AxiosInstance
  organization_id: string
  workspace_id: string
  default_view: MetricLevel
  is_admin: boolean
}

const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US').format(value)

// Sum a field across all metric types for a month
const sumMonthField = (
  metrics: MonthlyMetricsResponse,
  month: number,
  field: 'runCount' | 'uniqueValue' | 'totalValue'
): number =>
  Object.values(metrics).reduce((sum, rollups) => {
    if (!Array.isArray(rollups)) return sum
    const monthData = rollups.find((r) => r.month === month)
    return sum + (monthData ? monthData[field] : 0)
  }, 0)

// Calculate yearly totals from monthly data
const calcYearlyTotals = (
  metrics: MonthlyMetricsResponse,
  field: 'runCount' | 'uniqueValue' | 'totalValue'
): number =>
  Array.from({ length: 12 }, (_, i) => sumMonthField(metrics, i + 1, field)).reduce(
    (a, b) => a + b,
    0
  )

// Get unique doc count (uniqueEntities is stored in rollups)
const getUniqueDocCount = (metrics: MonthlyMetricsResponse): number => {
  const allEntities = new Set<string>()
  Object.values(metrics).forEach((rollups) => {
    if (!Array.isArray(rollups)) return
    rollups.forEach((r) => {
      r.uniqueEntities?.forEach((id) => allEntities.add(id))
    })
  })
  return allEntities.size
}

export function MetricsDashboard({
  api_instance,
  organization_id,
  workspace_id,
  default_view,
  is_admin,
}: MetricsDashboardProps) {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [activeTab, setActiveTab] = useState<MetricLevel>(
    !is_admin && (default_view === 'app' || default_view === 'org')
      ? 'workspace'
      : default_view
  )
  const [isExporting, setIsExporting] = useState(false)

  // Get accessible orgs/workspaces for cascading export
  const { accessibleOrganizations, accessibleWorkspaces } = useAuth()

  const handleTabChange = useCallback(
    (value: MetricLevel) => {
      if (!is_admin && (value === 'app' || value === 'org')) return
      setActiveTab(value)
    },
    [is_admin]
  )

  const { metrics, isLoading, error } = useMonthlyMetrics(
    api_instance,
    organization_id,
    workspace_id,
    activeTab,
    selectedYear
  )

  // Derived stats
  const totalRuns = useMemo(() => calcYearlyTotals(metrics, 'runCount'), [metrics])
  const totalPages = useMemo(() => calcYearlyTotals(metrics, 'uniqueValue'), [metrics])
  const uniqueDocs = useMemo(() => getUniqueDocCount(metrics), [metrics])

  const currentMonth = selectedYear === new Date().getFullYear() ? new Date().getMonth() + 1 : null

  // CSV export with cascading data fetch
  const handleExport = useCallback(async () => {
    setIsExporting(true)

    // Load savings settings from localStorage
    const RATE_PRESETS: Record<string, number> = {
      fast: 2, standard: 5, thorough: 10, legal: 15,
    }
    const { ratePreset, hourlyRate } = loadSavingsConfig()
    const minutesPerPage = RATE_PRESETS[ratePreset] || 5

    const rows: string[][] = [[
      'App', 'Organization', 'Workspace', 'Year', 'Month', 'Metric Type',
      'Runs', 'Unique Docs', 'Pages', 'Hours Saved', 'Hourly Rate', 'Estimated Savings',
    ]]
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ]

    const addMetricsRows = (
      metricsData: MonthlyMetricsResponse,
      orgId: string,
      wsId: string
    ) => {
      Object.entries(metricsData).forEach(([metricType, rollups]) => {
        if (!Array.isArray(rollups)) return
        rollups.forEach((r) => {
          if (r.month === null) return
          const pages = r.uniqueValue || 0
          const hoursSaved = (pages * minutesPerPage) / 60
          const estimatedSavings = hoursSaved * hourlyRate
          rows.push([
            'docreview',
            orgId,
            wsId,
            String(selectedYear),
            months[r.month - 1],
            r.metricDisplayName || metricType,
            String(r.runCount),
            String(r.uniqueEntities?.length || 0),
            String(pages),
            hoursSaved.toFixed(1),
            String(hourlyRate),
            estimatedSavings.toFixed(2),
          ])
        })
      })
    }

    try {
      const metricsApi = createMetricsApi(api_instance)

      if (activeTab === 'workspace') {
        // Workspace tab: just current workspace
        addMetricsRows(metrics, organization_id, workspace_id)
      } else if (activeTab === 'org') {
        // Org tab: fetch all workspaces in the current org
        const wsToFetch = accessibleWorkspaces.filter(ws => ws.orgId === organization_id)
        const results = await Promise.all(
          wsToFetch.map(ws =>
            metricsApi.getMonthlyMetrics(selectedYear, ws.orgId, ws.wsId, 'workspace')
              .then(data => ({ orgId: ws.orgId, wsId: ws.wsId, data }))
              .catch(() => null)
          )
        )
        results.forEach(r => {
          if (r) addMetricsRows(r.data, r.orgId, r.wsId)
        })
      } else if (activeTab === 'app') {
        // Global tab: fetch all workspaces across all orgs
        const allWorkspaces: Array<{ orgId: string; wsId: string }> = []
        for (const org of accessibleOrganizations) {
          const wsInOrg = accessibleWorkspaces.filter(ws => ws.orgId === org.orgId)
          wsInOrg.forEach(ws => allWorkspaces.push({ orgId: ws.orgId, wsId: ws.wsId }))
        }
        const results = await Promise.all(
          allWorkspaces.map(ws =>
            metricsApi.getMonthlyMetrics(selectedYear, ws.orgId, ws.wsId, 'workspace')
              .then(data => ({ orgId: ws.orgId, wsId: ws.wsId, data }))
              .catch(() => null)
          )
        )
        results.forEach(r => {
          if (r) addMetricsRows(r.data, r.orgId, r.wsId)
        })
      }
    } catch (err) {
      console.error('[MetricsDashboard] Export failed:', err)
    }

    const csv = rows.map((r) => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `metrics-${selectedYear}-${activeTab}.csv`
    a.click()
    URL.revokeObjectURL(url)

    setIsExporting(false)
  }, [metrics, selectedYear, organization_id, workspace_id, activeTab, api_instance, accessibleOrganizations, accessibleWorkspaces])

  const tabs: { id: MetricLevel; label: string }[] = [
    ...(is_admin ? [{ id: 'app' as const, label: 'Global App' }, { id: 'org' as const, label: 'Organization' }] : []),
    { id: 'workspace', label: 'Workspace' },
  ]

  const scopeTitle = activeTab === 'app' ? 'Global App Metrics' : activeTab === 'org' ? 'Organization Metrics' : 'Workspace Metrics'
  const scopePath = activeTab === 'app' ? null : activeTab === 'org' ? organization_id : `${organization_id}/${workspace_id}`

  return (
    <div className="text-slate-900">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[21px] font-[650] tracking-[-0.015em]">Document Processing Metrics</h1>
          <p className="mt-[5px] text-[13px] text-slate-500">Track volume and estimate ROI from automated document review</p>
        </div>
        <div className="flex items-center gap-2.5">
          <SegmentedTabs tabs={tabs} value={activeTab} onChange={handleTabChange} />
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger
              aria-label="Year"
              className="h-auto gap-2 rounded-[9px] border-slate-200 bg-white px-3.5 py-[7px] text-[12.5px] font-[550] text-slate-900 shadow-[0_1px_2px_rgba(2,8,23,0.04)]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[0, 1, 2].map((offset) => {
                const year = new Date().getFullYear() - offset
                return (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-7 mb-3.5 flex items-center gap-2.5">
        <span className="text-sm font-semibold tracking-[-0.01em]">{scopeTitle}</span>
        {scopePath && (
          <span className="rounded-md border border-slate-200 bg-slate-100 px-2 py-[3px] font-mono text-[11.5px] text-slate-600 break-all">
            {scopePath}
          </span>
        )}
        <span className="flex-1" />
        <button
          type="button"
          onClick={handleExport}
          disabled={isLoading || isExporting}
          className="inline-flex items-center gap-1.5 rounded-[9px] border border-slate-200 bg-white px-3.5 py-[7px] text-[12.5px] font-[550] text-slate-700 shadow-[0_1px_2px_rgba(2,8,23,0.04)] transition-colors duration-[120ms] hover:bg-slate-50 disabled:text-slate-400"
        >
          {isExporting
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Download className="h-3.5 w-3.5" />}
          {isExporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="overflow-hidden rounded-xl border border-[#e7ecf2] bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <div className="border-b border-[#eef2f6] px-6 py-5 sm:border-r lg:border-b-0">
            <div className="text-xs font-[550] text-slate-500">Total runs</div>
            <div className="mt-1.5 text-[27px] font-[650] tracking-[-0.02em] tabular-nums">{formatNumber(totalRuns)}</div>
            <div className="mt-[3px] text-xs text-slate-400">Pipeline executions in {selectedYear}</div>
          </div>
          <div className="border-b border-[#eef2f6] px-6 py-5 lg:border-b-0 lg:border-r">
            <div className="text-xs font-[550] text-slate-500">Unique documents</div>
            <div className="mt-1.5 text-[27px] font-[650] tracking-[-0.02em] tabular-nums">{formatNumber(uniqueDocs)}</div>
            <div className="mt-[3px] text-xs text-slate-400">Distinct documents processed</div>
          </div>
          <div className="border-b border-[#eef2f6] px-6 py-5 sm:border-b-0 sm:border-r">
            <div className="text-xs font-[550] text-slate-500">Pages processed</div>
            <div className="mt-1.5 text-[27px] font-[650] tracking-[-0.02em] tabular-nums">{formatNumber(totalPages)}</div>
            <div className="mt-[3px] text-xs text-slate-400">From unique documents</div>
          </div>
          <div className="px-6 py-5">
            <div className="text-xs font-[550] text-slate-500">Estimated fully loaded labor savings</div>
            <SavingsEstimate totalPages={totalPages} />
          </div>
        </div>
      </div>

      <MetricsTable
        metrics={metrics}
        isLoading={isLoading}
        year={selectedYear}
        currentMonth={currentMonth}
      />
    </div>
  )
}
